import { useEffect, useMemo, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import {
  analyzeTranscript,
  MAX_DECODER_CHARS,
  MAX_NOTES_CHARS,
  type ClipDetail,
  type CopyConfidence,
  type CopyMethod,
  type CopySaved,
  type GrammarIssue,
} from '@2x3/shared';
import { api, ApiError } from '../lib/api.js';
import { clearDraft, loadDraft, saveDraft, type Draft } from '../lib/drafts.js';
import TranscriptEditor from './TranscriptEditor.js';

interface Props {
  clip: ClipDetail;
  listened: MutableRefObject<number>;
  onSaved: (saved: CopySaved) => void;
}

const METHOD_TEXT: Record<CopyMethod, { label: string; help: string }> = {
  ear: { label: 'By ear', help: 'I copied it myself. No decoder was running.' },
  'ear+decoder': {
    label: 'By ear, checked with a decoder',
    help: 'I copied it by ear, then compared or corrected it against a decoder.',
  },
  decoder: { label: 'With a decoder', help: "It's mostly what a decoder printed, which I read over." },
};

const CONFIDENCE_TEXT: Record<CopyConfidence, { label: string; help: string }> = {
  high: { label: 'Solid', help: "I'd stake my call on it." },
  medium: { label: 'Mostly', help: 'The doubtful bits are marked.' },
  low: { label: 'Rough', help: 'Hard going; lots of guessing.' },
};

interface Fields {
  transcript: string;
  noCw: boolean;
  method: CopyMethod;
  decoder: string;
  confidence: CopyConfidence;
  notes: string;
}

function initialFields(clip: ClipDetail): { fields: Fields; restored: boolean } {
  const mine = clip.myCopy;
  const saved: Fields = mine
    ? {
        transcript: mine.transcript,
        noCw: mine.noCw,
        method: mine.method,
        decoder: mine.decoder ?? '',
        confidence: mine.confidence,
        notes: mine.notes ?? '',
      }
    : { transcript: '', noCw: false, method: 'ear', decoder: '', confidence: 'medium', notes: '' };

  const draft = loadDraft(clip.id);
  if (draft && (!mine || draft.savedAt > mine.updatedAt)) {
    const fields: Fields = {
      transcript: draft.transcript,
      noCw: draft.noCw,
      method: draft.method,
      decoder: draft.decoder,
      confidence: draft.confidence,
      notes: draft.notes,
    };
    return { fields, restored: true };
  }
  return { fields: saved, restored: false };
}

export default function CopyForm({ clip, listened, onSaved }: Props) {
  const initial = useMemo(() => initialFields(clip), [clip]);
  const [f, setF] = useState<Fields>(initial.fields);
  const [restored, setRestored] = useState(initial.restored);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<GrammarIssue[]>([]);
  const [dirty, setDirty] = useState(initial.restored);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const set = <K extends keyof Fields>(key: K, value: Fields[K]): void => {
    setF((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setServerIssues([]);
  };

  // Keep an unsaved copy in this browser, so a long one survives a closed tab.
  useEffect(() => {
    if (!dirty) return;
    const timer = setTimeout(() => {
      const draft: Draft = { ...f, savedAt: new Date().toISOString() };
      saveDraft(clip.id, draft);
    }, 400);
    return () => clearTimeout(timer);
  }, [f, dirty, clip.id]);

  const analysis = useMemo(() => analyzeTranscript(f.transcript), [f.transcript]);
  const needsDecoderName = f.method !== 'ear' && f.decoder.trim() === '';
  const canSave = !saving && (f.noCw || analysis.ok) && !needsDecoderName;

  const submit = async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await api.saveCopy(clip.id, {
        transcript: f.noCw ? '' : f.transcript,
        noCw: f.noCw,
        method: f.method,
        decoder: f.method === 'ear' ? null : f.decoder.trim(),
        confidence: f.confidence,
        notes: f.notes.trim() || null,
        listenedSec: Math.round(listened.current),
      });
      clearDraft(clip.id);
      setDirty(false);
      setRestored(false);
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setServerIssues(err.issues);
      } else {
        setError('The copy could not be sent. Check your connection and try again - your draft is kept here.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Ctrl+Enter (Cmd+Enter on a Mac) saves from anywhere in the form.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && formRef.current?.contains(e.target as Node)) {
        e.preventDefault();
        void submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const mine = clip.myCopy;

  return (
    <form ref={formRef} className="copy-form" onSubmit={(e) => void submit(e)}>
      <div className="copy-form-head">
        <h2>Your copy</h2>
        {mine && !dirty && (
          <span className="badge is-good">
            Saved{mine.revision > 1 ? ` (revision ${mine.revision})` : ''}
          </span>
        )}
        {restored && <span className="badge">Unsaved draft restored from this browser</span>}
      </div>

      <label className="check no-cw">
        <input type="checkbox" checked={f.noCw} onChange={(e) => set('noCw', e.target.checked)} />
        <span>
          <strong>There's no CW in this clip</strong> <span className="muted">- only noise, or a signal that isn't Morse.</span>{' '}
          If there is CW you can't copy, type <code>#</code> instead.
        </span>
      </label>

      <TranscriptEditor
        value={f.transcript}
        onChange={(v) => set('transcript', v)}
        disabled={f.noCw}
        textareaRef={textareaRef}
      />

      <fieldset className="choice">
        <legend>How did you copy it?</legend>
        {(Object.keys(METHOD_TEXT) as CopyMethod[]).map((m) => (
          <label key={m} className="radio">
            <input type="radio" name="method" checked={f.method === m} onChange={() => set('method', m)} />
            <span>
              <strong>{METHOD_TEXT[m].label}</strong> <span className="muted">{METHOD_TEXT[m].help}</span>
            </span>
          </label>
        ))}
        {f.method !== 'ear' && (
          <label className="field">
            <span>Which decoder?</span>
            <input
              type="text"
              value={f.decoder}
              maxLength={MAX_DECODER_CHARS}
              onChange={(e) => set('decoder', e.target.value)}
              placeholder="e.g. fldigi 4.2, CW Skimmer 2.1"
              required
            />
            <span className="muted small">
              Decoder-assisted copies are welcome. They are filed apart from ear copies, which is why the name matters.
            </span>
          </label>
        )}
      </fieldset>

      <fieldset className="choice choice-row">
        <legend>How sure are you, overall?</legend>
        {(Object.keys(CONFIDENCE_TEXT) as CopyConfidence[]).map((c) => (
          <label key={c} className="radio">
            <input
              type="radio"
              name="confidence"
              checked={f.confidence === c}
              onChange={() => set('confidence', c)}
            />
            <span>
              <strong>{CONFIDENCE_TEXT[c].label}</strong> <span className="muted">{CONFIDENCE_TEXT[c].help}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <label className="field">
        <span>
          Notes <span className="muted">(optional)</span>
        </span>
        <textarea
          value={f.notes}
          maxLength={MAX_NOTES_CHARS}
          rows={2}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="QSB, a second station, a bug fist, where you lost it..."
        />
      </label>

      {error && (
        <div className="alert is-error" role="alert">
          {error}
          {serverIssues.length > 0 && (
            <ul>
              {serverIssues.map((i, n) => (
                <li key={n}>
                  {i.snippet && <code>{i.snippet}</code>} {i.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={!canSave}>
          {saving ? 'Saving...' : mine ? 'Update my copy' : 'Save my copy'}
        </button>
        <span className="muted small">
          <kbd>Ctrl</kbd>+<kbd>Enter</kbd> saves.
          {!f.noCw && !analysis.ok && f.transcript.trim() !== '' && ' Fix the marked problems first.'}
          {needsDecoderName && ' Name the decoder first.'}
        </span>
      </div>
    </form>
  );
}
